import { Component, OnDestroy, type OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { AlertService } from 'src/app/components/alert/alert.service';
import { ISearchEvent } from 'src/app/components/search-form/search-form.constants';
import { PolicyService } from 'src/app/modules/policy/policy.service';
import { ITableEvent, ITableKey } from 'src/app/modules/shared/components/basic-table/basic-table.constants';
import { IFormKey } from 'src/app/modules/shared/models/forms';
import { IDashboardOperation, IDashboardOperationRequest, IKpiResponse, IOperationKPI } from 'src/app/modules/shared/models/operation.model';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { LoggerService } from 'src/app/services/logger.service';
import { SpinnerService } from 'src/app/services/spinner.service';
import { CommonUtils } from 'src/app/utils/CommonUtils';
import { FormUtils } from 'src/app/utils/FormUtils';
import { dashboard_table_keys, ongoing_operations } from '../dashboard/dashboard.constants';
import { DashboardService } from '../dashboard/dashboard.service';
import { HOME_ALERT_MESSAGES, home_form_keys, home_form_template, HOME_OPERATIONS_LIMIT } from './home.constants';

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss',
})
export class HomeComponent implements OnInit, OnDestroy {

  form: FormGroup;
  formKeys: IFormKey[] = CommonUtils.assign(home_form_keys);
  tableKeys: ITableKey[] = CommonUtils.assign(dashboard_table_keys);
  lastOperations: IDashboardOperation[] = [];
  kpis: IOperationKPI[] = [];
  countersType: 'number' | 'premium';
  destroy$ = new Subject();

  constructor(
    private readonly _router: Router,
    private readonly _spinner: SpinnerService,
    private readonly _authService: AuthenticationService,
    private readonly _dashboardService: DashboardService,
    private readonly _alertService: AlertService,
    private readonly _policyService: PolicyService
  ) { }

  ngOnInit(): void {
    if (this.lastOperations) {
      this.lastOperations = this.lastOperations.map(elem => ({ ...elem, showIcon: true }));
    }
    this.createForm();
    this.getKpis();
    this.getLastOperations();
    localStorage.removeItem('movementId');
    this._policyService.detailLoaded(null);
  }

  createForm() {
    this.form = FormUtils.initFormGroupFromTemplate(home_form_template);
  }

  navigate(destination: string) {
    this._router.navigate([destination]);
  }

  onSearchRequested(event: ISearchEvent) {
    this._router.navigate(['/search'], {
      state: event
    })
  }

  onDetailRequested(tableObject: ITableEvent<IDashboardOperation>): void {
    LoggerService.logInfo(this.constructor.name, 'onDetailRequested', { tableObject });
    const operationStatus = CommonUtils.checkAndGet(tableObject, 'element.status', null);
    if (ongoing_operations.includes(operationStatus)) {
      const companyId = CommonUtils.getCompanyCode(tableObject?.element?.companyId);
      if (companyId) {
        this._router.navigate([`/policies/${tableObject?.element?.bperContractNumber}/${companyId}/operations/${tableObject?.element?.operationId}`], {
          state: tableObject
        });
      } else {
        this._alertService.openErrorMessage('Codice compagnia non riconosciuto', '');
      }
    } else {
      this._router.navigate([`/policies/${tableObject?.element?.bperContractNumber}`]);
    }
  }

  getLastOperations() {
    this._spinner.show();
    const body: IDashboardOperationRequest = {
      companyCode: this._authService.dashboardMode === 'F' ? this._authService.loggedUser.CurrentBank : null,
      pageSize: HOME_OPERATIONS_LIMIT,
      pageIndex: 0
    }
    LoggerService.logInfo(this.constructor.name, 'getLastOperations', { body });
    this._dashboardService.getDashboardOperations(body)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: resp => {
          LoggerService.logInfo(this.constructor.name, 'getLastOperations', { resp });
          this.lastOperations = CommonUtils.manageDashboardData(CommonUtils.checkAndGet(resp, 'data', null));
          this._spinner.hide();
        },
        error: error => {
          LoggerService.logError(this.constructor.name, 'getLastOperations', { error });
          this._alertService.openErrorMessage('', 'getLastOperations', error);
          this._spinner.hide();
        }
      })
  }

  getKpis() {
    this._spinner.show();
    this._dashboardService.getMonthlyKpis()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: IKpiResponse) => {
          LoggerService.logInfo(this.constructor.name, 'getKpis', { response });
          this.countersType = CommonUtils.checkAndGet(response, 'type', null);
          this.kpis = CommonUtils.manageGeneralKpis(CommonUtils.checkAndGet(response, 'generale', null));
          this._spinner.hide();
        },
        error: error => {
          LoggerService.logError(this.constructor.name, 'getKpis', { error });
          this._alertService.openErrorMessage('', 'getKpis', error);
          this._spinner.hide();
        }
      })
  }

  get showKpis(): boolean {
    return !this._authService.hideKpis;
  }

  get alertMessages(): Record<string, string>[] {
    return HOME_ALERT_MESSAGES;
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

}
